import React, { useState } from 'react';
import { Button, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconSparkles } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import { askClarifyingQuestions, summariseJob } from '../../services/ServiceRequestService';
import { IClarifyingQuestion, IServiceRequestClarification } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text } = Typography;

/** Same floor the backend refuses below. */
const MIN_DESCRIPTION_LENGTH = 15;

interface JobClarifierProps {
  description: string;
  trade?: string;

  /**
   * The listing this is about, so the professional's own standing questions
   * get asked too -- see AssistantGuideEditor. Absent when the customer is
   * asking the professional rather than one of their adverts, which asks the
   * shared defaults instead.
   */
  serviceId?: string;
  /** Raised whenever the answers or the summary change, so the page can send them along. */
  onChange: (clarifications: IServiceRequestClarification[], summary?: string) => void;
}

/**
 * Two or three questions that turn "se me ha roto un cable" into something
 * an electrician can price without ringing anybody up.
 *
 * It sits BEFORE the send button, on the customer's own screen, and that
 * placement is the whole design: in the chat afterwards the customer believes
 * they are talking to the professional, and a model writing there either
 * impersonates them or becomes a strange third voice. Here nobody pretends to
 * be anybody -- this is helping somebody write their own request.
 *
 * Nothing here ever blocks sending. Every question is skippable, "no lo sé"
 * is always an option, and a customer who ignores this entirely sends exactly
 * the request they would have sent before.
 */
const JobClarifier: React.FC<JobClarifierProps> = ({ description, trade, serviceId, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const [questions, setQuestions] = useState<IClarifyingQuestion[]>();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string>();
  const [asking, setAsking] = useState(false);
  const [summarising, setSummarising] = useState(false);
  // Set once the backend says the assistant is not configured here. The block
  // puts itself away rather than offering a button that cannot work.
  const [unavailable, setUnavailable] = useState(false);

  const handleAsk = async () => {
    if (description.trim().length < MIN_DESCRIPTION_LENGTH) {
      message.error(translate(t, TRANSLATIONS.SERVICE_REQUEST_DESCRIPTION_TOO_SHORT));
      return;
    }

    setAsking(true);
    try {
      const outcome = await askClarifyingQuestions(description.trim(), trade, serviceId);
      if (!outcome.ok) {
        if (outcome.unavailable) {
          setUnavailable(true);
          return;
        }
        // The customer gets the translated line; the server's own text is
        // English and written for us, not for somebody with a leaking tap.
        // It goes to the console, where it is still there when you need it.
        if (outcome.error) console.error('Clarifying questions failed:', outcome.error);
        message.error(translate(t, TRANSLATIONS.JOB_CLARIFIER_ERROR));
        return;
      }
      setQuestions(outcome.value);
      // Nothing to ask is a real answer, not a failure: the description
      // already said enough, and saying so is friendlier than silence.
      if (outcome.value.length === 0) message.success(translate(t, TRANSLATIONS.JOB_CLARIFIER_NOTHING_TO_ASK));
    } finally {
      setAsking(false);
    }
  };

  const pick = async (question: string, option: string) => {
    const next = { ...answers, [question]: option };
    setAnswers(next);

    const pairs = (questions ?? [])
      .map((q) => ({ question: q.question, answer: next[q.question] }))
      .filter((pair): pair is IServiceRequestClarification => Boolean(pair.answer));

    onChange(pairs, summary);

    // Only once every question has been answered: summarising after each tap
    // would spend a call per tap and show a summary that keeps changing under
    // somebody who is still answering.
    if (questions && pairs.length === questions.length && questions.length > 0) {
      setSummarising(true);
      try {
        const outcome = await summariseJob(description.trim(), pairs);
        if (outcome.ok) {
          setSummary(outcome.value);
          onChange(pairs, outcome.value);
        }
        // A failed summary is not worth a message: the answers are the
        // valuable part and they are already attached.
      } finally {
        setSummarising(false);
      }
    }
  };

  if (unavailable) return null;

  return (
    <div style={{ padding: 12, marginBottom: 20, borderRadius: 14, background: TEAL_SOFT_BG }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <IconSparkles size={15} style={{ color: TEAL }} />
        <Text strong style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.JOB_CLARIFIER_TITLE)}
        </Text>
      </div>

      {questions === undefined ? (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
            {translate(t, TRANSLATIONS.JOB_CLARIFIER_HINT)}
          </Text>
          <Button block loading={asking} onClick={handleAsk} style={{ borderRadius: 999 }}>
            {translate(t, TRANSLATIONS.JOB_CLARIFIER_ASK)}
          </Button>
        </>
      ) : (
        questions.map((q) => (
          <div key={q.question} style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>{q.question}</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {/* "No lo sé" is added here rather than expected from the model,
                  and it is a useful answer in its own right: it tells the
                  professional that bit has to be seen on site. */}
              {[...q.options, translate(t, TRANSLATIONS.JOB_CLARIFIER_DONT_KNOW)].map((option) => {
                const selected = answers[q.question] === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => pick(q.question, option)}
                    style={{
                      border: `1px solid ${selected ? TEAL : token.colorBorder}`,
                      background: selected ? TEAL : token.colorBgContainer,
                      color: selected ? '#fff' : token.colorText,
                      borderRadius: 999,
                      padding: '6px 14px',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      {(summary || summarising) && (
        <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 10, background: token.colorBgContainer }}>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
            {translate(t, TRANSLATIONS.JOB_CLARIFIER_SUMMARY_TITLE)}
          </Text>
          <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {summarising ? translate(t, TRANSLATIONS.JOB_CLARIFIER_SUMMARISING) : summary}
          </Text>
        </div>
      )}

      {questions !== undefined && questions.length > 0 && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
          {translate(t, TRANSLATIONS.JOB_CLARIFIER_SKIPPABLE)}
        </Text>
      )}
    </div>
  );
};

export default JobClarifier;
